import mixpanel from 'mixpanel-browser';
import dayjs from 'dayjs';
import { createAction, handleActions } from 'redux-actions';

import Agent from '../../api/Agent';
import history from '../../config/history';
import { handleError } from '../user';

import mixpanelConstants from '../../constants/mixpanel';
import { toastr } from 'react-redux-toastr';

export const setPage = createAction('SET_PAGE');
export const setRoles = createAction('SET_ROLES');
export const setState = createAction('SET_STATE');
export const setFilter = createAction('SET_FILTER');
export const setNewShift = createAction('SET_NEW_SHIFT');
export const setShiftList = createAction('SET_SHIFT_LIST');
export const setSupervisor = createAction('SET_SUPERVISOR');
export const clearAllFilters = createAction('CLEAR_FILTERS');
export const setShiftTimes = createAction('SET_SHIFT_TIMES');
export const clearShiftForm = createAction('CLEAR_SHIFT_FORM');
export const setSelectedUser = createAction('SET_SELECTED_USER');
export const setIsLoadingUser = createAction('SET_IS_LOADING_USER');
export const setLocation = createAction('SET_ASSIGN_SHIFT_LOCATION');
export const setIsLoadingShifts = createAction('SET_IS_LOADING_SHIFTS');
export const setDepartment = createAction('SET_ASSIGN_SHIFT_DEPARTMENT');
export const setOrganization = createAction('SET_ASSIGN_SHIFT_ORGANIZATION');
export const setIsSubmittingShift = createAction('SET_IS_SUBMITTING_SHIFT');

const INITIAL_STATE = {
	departments: [],
	filters: {
		department: '',
		fromDate: null,
		location: '',
		organization: '',
		page: 1,
		role: '',
		sortBy: 'id',
		sortOrder: 'desc',
		state: '',
		status: '',
		supervisor: '',
		toDate: null
	},
	isLoadingShifts: false,
	isLoadingUser: false,
	isSubmittingShift: false,
	locations: [],
	newShift: {
		department: '',
		durationExternal: '',
		durationFullTime: '',
		durationPartTime: '',
		endDate: null,
		endTime: null,
		has_covid_patients: null,
		isExternalNotifyEnabled: true,
		isFullTimeNotifyEnabled: true,
		isPartTimeNotifyEnabled: true,
		location: '',
		notes: '',
		openPositions: '1',
		organization: '',
		prn_pool_duration_in_days: '',
		prn_pool_enabled: false,
		roles: [],
		shiftTime: null,
		startDate: null,
		startTime: null,
		supervisor: ''
	},
	organizations: [],
	roles: [],
	shiftList: {},
	shiftTimes: [],
	states: [],
	supervisors: [],
	user: {}
};

export default handleActions(
	{
		CLEAR_FILTERS: (state) => ({ ...state, filters: INITIAL_STATE.filters }),
		CLEAR_SHIFT_FORM: (state) => ({ ...state, newShift: INITIAL_STATE.newShift }),
		SET_ASSIGN_SHIFT_DEPARTMENT: (state, action) => ({ ...state, departments: action.payload }),
		SET_ASSIGN_SHIFT_LOCATION: (state, action) => ({ ...state, locations: action.payload }),
		SET_ASSIGN_SHIFT_ORGANIZATION: (state, action) => ({ ...state, organizations: action.payload }),
		SET_FILTER: (state, action) => ({
			...state,
			filters: { ...state.filters, [action.payload.name]: action.payload.value }
		}),
		SET_IS_LOADING_SHIFTS: (state, action) => ({ ...state, isLoadingShifts: action.payload }),
		SET_IS_LOADING_USER: (state, action) => ({ ...state, isLoadingUser: action.payload }),
		SET_IS_SUBMITTING_SHIFT: (state, action) => ({ ...state, isSubmittingShift: action.payload }),
		SET_NEW_SHIFT: (state, action) => ({
			...state,
			newShift: { ...state.newShift, [action.payload.name]: action.payload.value }
		}),
		SET_PAGE: (state, action) => ({ ...state, filters: { ...state.filters, page: action.payload } }),
		SET_ROLES: (state, action) => ({ ...state, roles: action.payload }),
		SET_SELECTED_USER: (state, action) => ({ ...state, user: action.payload }),
		SET_SHIFT_LIST: (state, action) => ({ ...state, shiftList: action.payload }),
		SET_SHIFT_TIMES: (state, action) => ({ ...state, shiftTimes: action.payload }),
		SET_STATE: (state, action) => ({ ...state, states: action.payload }),
		SET_SUPERVISOR: (state, action) => ({ ...state, supervisors: action.payload })
	},
	INITIAL_STATE
);

export const clearFilters = () => (dispatch) => dispatch(clearAllFilters());

export const getOrganizationShiftTimes = (orgId) => (dispatch) => {
	return Agent.getOrgShiftTimes(orgId).then((response) => {
		if (response.ok) {
			dispatch(setShiftTimes(response.data));
		} else {
			dispatch(handleError(response));
		}
	});
};

export const getOrganization = (callback) => (dispatch) => {
	Agent.organization().then((response) => {
		if (response.ok) {
			response.data.sort(function (a, b) {
				if (a.title.toLowerCase() < b.title.toLowerCase()) return -1;
				if (a.title.toLowerCase() > b.title.toLowerCase()) return 1;
				return 0;
			});
			callback(response.data);
			dispatch(setOrganization(response.data));
		} else {
			dispatch(handleError(response));
		}
	});
};

export const setLocationList = (orgId) => (dispatch, getState) => {
	dispatch(setLocation([]));
	dispatch(setDepartment([]));

	const orgs = getState().assignShift.organizations.find((org) => +orgId === org.organization_id);

	if (!orgId || !orgs) {
		dispatch(setFilter({ name: 'location', value: '' }));
		dispatch(setFilter({ name: 'department', value: '' }));
		return;
	}

	const prevLocId = +getState().assignShift.filters.location;

	const orgHasLocation = orgs.locations.find((loc) => loc.location_id === prevLocId);

	dispatch(setLocation(orgs.locations));

	if (orgHasLocation) {
		dispatch(setDepartmentList(prevLocId));
		return;
	}

	dispatch(setFilter({ name: 'location', value: '' }));
	dispatch(setFilter({ name: 'department', value: '' }));
};

export const setDepartmentList = (locId) => (dispatch, getState) => {
	dispatch(setDepartment([]));

	const locs = getState().assignShift.locations.find((loc) => +locId === loc.location_id);

	if (!locId || !locs) {
		dispatch(setFilter({ name: 'department', value: '' }));
		return;
	}

	const prevDeptId = +getState().assignShift.filters.department;

	const locHasDepartment = locs.departments.find((dept) => dept.department_id === prevDeptId);

	dispatch(setDepartment(locs.departments));

	if (!locHasDepartment) {
		dispatch(setFilter({ name: 'department', value: '' }));
	}
};

export const getRoles = () => (dispatch) =>
	Agent.roleOptions().then((response) => {
		if (response.ok) {
			dispatch(setRoles(response.data || []));
		} else {
			dispatch(handleError(response));
		}
	});

export const getShiftList = (id) => (dispatch, getState) => {
	const filters = getState().assignShift.filters;
	let nurseId = getState().assignShift.user.id || id;
	nurseId = nurseId || '';

	if (nurseId == '') {
		return;
	}

	const data = {
		cancel_reason: null,
		dept: filters.department,
		from_date: filters.fromDate ? dayjs(filters.fromDate).format('MM-DD-YYYY') : '',
		loc: filters.location,
		nurse_id: nurseId,
		org: filters.organization,
		page_no: filters.page,
		role: filters.role,
		search_string: '',
		shift_status: filters.status,
		sort: filters.sortBy,
		sortOrder: filters.sortOrder,
		state: filters.state,
		supervisor_id: filters.supervisor,
		to_date: filters.toDate ? dayjs(filters.toDate).format('MM-DD-YYYY') : ''
	};

	dispatch(setIsLoadingShifts(true));

	return Agent.getShiftsWithPaging(data).then((response) => {
		dispatch(setIsLoadingShifts(false));
		if (response.ok) {
			const data = response.data;
			const shifts = (data.shifts || []).filter((shift) => shift.shift_status !== 'Canceled') || [];
			dispatch(setShiftList({ ...data, shifts }));
		} else {
			dispatch(handleError(response));
		}
	});
};

export const trackAssignShift = (mixpanelConstant, userId, shiftDetails = {}, otherProps = {}) => {
	mixpanel.identify(userId);
	mixpanel.track(mixpanelConstant, { ...shiftDetails, ...otherProps, origin: 'Admin assign shift' });
};

export const assignNurseToShift = (shiftId, nurseId) => (dispatch, getState) => {
	dispatch(setIsLoadingShifts(true));

	return Agent.assignNurseToShift(shiftId, nurseId).then((response) => {
		dispatch(setIsLoadingShifts(false));
		if (response.ok) {
			trackAssignShift(mixpanelConstants.APPLY_SHIFT, nurseId, { shiftId });

			const loggedInUser = getState().user;
			trackAssignShift(mixpanelConstants.ASSIGN_SHIFT_TO_NURSE, loggedInUser.id, { nurseId, shiftId });

			history.push(`/supervisor/supervisorshiftdetails/${shiftId}`);
		} else {
			if (typeof response.data === 'string') {
				toastr.error(response.data);
			} else {
				dispatch(handleError(response));
			}
		}
	});
};

export const trackCreateAndApplyShift = (data) => {
	const shiftData = {
		aftDelay: data.aft_delay,
		allowExternalNurses: data.allow_external_nurses,
		allowFullTimeNurses: data.allow_full_time_nurses,
		allowPartTimeNurses: data.allow_part_time_nurses,
		aoaDelay: data.aoa_delay,
		aptDelay: data.apt_delay,
		departmentId: data.department_id,
		has_covid_patients: data.has_covid_patients,
		locationId: data.location_id,
		nurseId: data.nurse_id,
		nurseRole: data.nurse_role,
		openPositions: data.open_positions,
		organizationId: data.organization_id,
		prn_pool_duration_in_days: data.prn_pool_duration_in_days,
		prn_pool_enabled: data.prn_pool_enabled,
		shiftDate: data.shift_date,
		shiftEnd: data.shift_end,
		shiftEndDate: data.shift_end_date,
		shiftNotes: data.shift_notes,
		shiftStart: data.shift_start
	};

	mixpanel.track(mixpanelConstants.CREATE_AND_ASSIGN_SHIFT_TO_NURSE, shiftData);
};

export const createShiftAndAssign = (data) => (dispatch) => {
	dispatch(setIsSubmittingShift(true));

	return Agent.createShiftAndAssign(data).then((response) => {
		dispatch(setIsSubmittingShift(false));
		if (response.ok) {
			trackCreateAndApplyShift(data);

			history.push(`/supervisor/supervisorshiftdetails/${response.data}`);
		} else {
			if (response.status === 400) {
				toastr.error('An error occurred', response.data);
			} else {
				dispatch(handleError(response));
			}
		}
	});
};

export const getSupervisorList = (data) => (dispatch, getState) => {
	const getAll = data.all;
	const orgChanged = data.organization;

	let location = getState().assignShift.filters.location !== '' ? getState().assignShift.filters.location : 'NA';
	let organization =
		getState().assignShift.filters.organization !== '' ? getState().assignShift.filters.organization : 'NA';

	if (orgChanged) {
		location = 'NA';
	}

	Agent.supervisors(getAll ? 'NA' : organization, getAll ? 'NA' : location).then((response) => {
		if (response.ok) {
			dispatch(setSupervisorList(response.data));
		} else {
			dispatch(handleError(response));
		}
	});
};

export const setSupervisorList = (list) => (dispatch, getState) => {
	const prevSupervisor = getState().assignShift.filters.supervisor;

	const supervisor = list.find((s) => +s.id === +prevSupervisor);

	if (!supervisor) {
		dispatch(setFilter({ name: 'supervisor', value: '' }));
		dispatch(getShiftList());
	}

	dispatch(setSupervisor(list));
};

export const getUserInfo = (id) => (dispatch) => {
	dispatch(setIsLoadingUser(true));

	Agent.getUserInfoById(id).then((response) => {
		if (response.ok) {
			dispatch(setSelectedUser(response.data));
		} else {
			dispatch(handleError(response));
		}
		dispatch(setIsLoadingUser(false));
	});
};
